<template>
  <teleport to="body">
    <div
      ref="authorHolder"
      class="author-holder px-4 py-3 z-[9999] fixed bg-white/95 dark:bg-[#0d1a20]/95 backdrop-blur-xl border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 shadow-2xl rounded-xl transition-opacity duration-200"
      :class="isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'"
      :style="{
        width: width + 'px',
        visibility: isVisible ? 'visible' : 'hidden',
      }"
      :data-ref-id="refId"
      @mouseenter="mouseIsOver = true"
      @mouseleave="mouseIsOver = false"
    >
      <div v-if="loading" class="text-center py-6">
        <i class="text-2xl fas fa-circle-notch fa-spin text-emerald-500"></i>
      </div>
      <div v-else-if="selectedAuthor">
        <div class="flex items-center mb-3">
          <img
            :src="avatarUrlFromString(selectedAuthor.name)"
            class="w-10 h-10 rounded-full shrink-0 mr-3 shadow-md border-2 border-white dark:border-gray-800"
          />
          <div class="overflow-hidden">
            <h3
              class="font-bold text-sm leading-tight cursor-pointer hover:text-emerald-600 transition-colors"
              @click="scrollToExpert(refId)"
            >
              {{ selectedAuthor.name }}
            </h3>
            <div
              v-if="selectedAuthor.affiliations?.length > 0"
              class="text-[10px] text-gray-500 dark:text-gray-400 italic mt-0.5"
            >
              {{ selectedAuthor.affiliations[0] }}
            </div>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-1.5 mb-3">
          <span
            v-if="selectedAuthor.h_index"
            class="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded font-mono font-bold text-[9px] border border-gray-200 dark:border-gray-700"
          >
            h-index: {{ selectedAuthor.h_index }}
          </span>
          <span
            v-if="selectedAuthor.citation_count"
            class="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50 px-1.5 py-0.5 rounded text-[9px] font-bold flex items-center"
          >
            <i class="fa-solid fa-quote-right text-[8px] mr-1 opacity-60"></i>
            {{ selectedAuthor.citation_count.toLocaleString() }}
          </span>
        </div>
        <div
          class="flex justify-end pt-2 border-t border-gray-100 dark:border-gray-800"
        >
          <a
            target="_blank"
            :href="`/research-compass/author/${sqidEncode(
              selectedAuthor.authorId,
            )}`"
            class="text-emerald-600 dark:text-emerald-400 text-[10px] font-bold hover:underline flex items-center gap-1"
          >
            View Profile <i class="fas fa-arrow-right text-[8px]"></i>
          </a>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { createPopper } from "@popperjs/core";
import * as utils from "../../utils";

const { avatarUrlFromString, sqidEncode } = utils;

export default {
  name: "ResearchCompassAuthorPopover",
  props: {
    question: Object,
    width: {
      type: Number,
      default: 240,
    },
  },
  emits: ["scroll-to-expert"],
  data() {
    return {
      isVisible: false,
      mouseIsOver: false,
      loading: true,
      refId: null,
      selectedAuthor: null,
      popperInstance: null,
    };
  },
  computed: {
    orderedAuthors() {
      if (!this.question?.authors) return [];
      return Object.values(this.question.authors).sort((a, b) => {
        const scoreA = a.normalizedFitScore || 0;
        const scoreB = b.normalizedFitScore || 0;
        if (scoreA !== scoreB) {
          return scoreB - scoreA;
        }
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      });
    },
  },
  watch: {
    mouseIsOver(newVal) {
      if (!newVal) {
        setTimeout(() => {
          if (!this.mouseIsOver) {
            this.hide();
          }
        }, 200);
      }
    },
  },
  methods: {
    show(targetRect) {
      this.isVisible = true;
      this.mouseIsOver = true;

      const virtualElement = {
        getBoundingClientRect: () => targetRect,
      };

      if (this.popperInstance) {
        this.popperInstance.destroy();
      }

      this.$nextTick(() => {
        if (!this.$refs.authorHolder) return;
        this.popperInstance = createPopper(
          virtualElement,
          this.$refs.authorHolder,
          {
            strategy: "fixed",
            placement: "top",
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 2],
                },
              },
              {
                name: "flip",
                options: {
                  fallbackPlacements: ["bottom", "top"],
                },
              },
              {
                name: "preventOverflow",
                options: {
                  boundary: document.body,
                  padding: 16,
                },
              },
            ],
          },
        );
      });
    },
    hide() {
      this.isVisible = false;
      if (this.popperInstance) {
        this.popperInstance.destroy();
        this.popperInstance = null;
      }
    },
    hideIfNotHover() {
      if (!this.mouseIsOver) {
        this.hide();
      }
    },
    load() {
      this.loading = true;
      const nameToFind = this.refId
        ? decodeURIComponent(this.refId).toLowerCase()
        : "";
      this.selectedAuthor = this.orderedAuthors.find(
        (a) => a.name.toLowerCase() === nameToFind,
      );
      this.loading = false;
    },
    avatarUrlFromString(name) {
      return avatarUrlFromString(name);
    },
    sqidEncode(id) {
      return sqidEncode(id);
    },
    scrollToExpert(refId) {
      this.$emit("scroll-to-expert", refId);
      this.hide();
    },
  },
};
</script>
